import { useEffect, useState, type FormEvent } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { Channel } from "../sync/types";
import { Badge, EmptyState, Field, Form, Sheet, Switch } from "./ui";
import { t } from "../i18n";

type Tab = "browse" | "create" | "dm";

// Browse finds channels to join, makes new ones, and opens direct messages.
export function Browse({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const state = useLantern();
  // Guests never create channels; others unless only admins may.
  const role = state.me?.role;
  const mayCreate = role === "admin" || (role !== "guest" && state.channelCreation === "everyone");
  const [chosen, setTab] = useState<Tab>("browse");
  // Creating, until only admins may.
  const tab = chosen === "create" && !mayCreate ? "browse" : chosen;
  const [channels, setChannels] = useState<Channel[] | null>(null);
  const [filter, setFilter] = useState("");
  const [name, setName] = useState("");
  const [priv, setPriv] = useState(false);
  const [error, setError] = useState("");
  const [people, setPeople] = useState("");

  useEffect(() => {
    session.api.browse().then((r) => setChannels(r.channels), () => setError(t("Could not list channels.")));
  }, [session]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const { channel } = await session.api.createChannel(name.trim().replace(/^#/, ""), priv ? "private" : "public");
      onClose();
      navigate(`/c/${channel.id}`);
    } catch {
      setError(t("Could not create the channel. Names use lowercase letters, digits, - and _."));
    }
  }

  async function join(c: Channel) {
    setError("");
    try {
      if (!c.membership) await session.api.joinChannel(c.id);
      onClose();
      navigate(`/c/${c.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function dm(e: FormEvent) {
    e.preventDefault();
    setError("");
    const handles = people.split(/[\s,]+/).map((h) => h.replace(/^@/, "")).filter(Boolean);
    const ids = [...session.directory.values()].filter((p) => handles.includes(p.handle)).map((p) => p.id);
    if (ids.length !== handles.length) {
      setError(t("Some of those handles do not exist."));
      return;
    }
    try {
      const { channel } = await session.api.openDM(ids);
      await session.loadDMMembers();
      onClose();
      navigate(`/c/${channel.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const q = filter.trim().toLowerCase().replace(/^#/, "");
  const shown = (channels ?? [])
    .filter((c) => (c.kind === "public" || c.membership) && c.name)
    .filter((c) => !q || c.name!.toLowerCase().includes(q) || c.topic?.toLowerCase().includes(q))
    .sort((a, b) => a.name!.localeCompare(b.name!));

  const tabs: [Tab, string][] = [["browse", t("Browse")], ...(mayCreate ? [["create", t("New channel")] as [Tab, string]] : []), ["dm", t("Message people")]];

  return (
    <Sheet title={t("Channels")} onClose={onClose} className="page browse-sheet">
      <div className="segmented" role="tablist">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            className={tab === id ? "active" : ""}
            onClick={() => {
              setTab(id);
              setError("");
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {error && <p className="error">{error}</p>}

      {tab === "browse" && (
        <>
          <input className="browse-filter" type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("Find a channel")} aria-label={t("Find a channel")} autoFocus />
          {channels && shown.length === 0 ? (
            <EmptyState icon="#" title={q ? t("No channel matches") : t("No channels yet")}>
              {mayCreate && (
                <button className="link" onClick={() => setTab("create")}>
                  {t("Create one")}
                </button>
              )}
            </EmptyState>
          ) : (
            <ul className="rows browse">
              {shown.map((c) => (
                <li key={c.id}>
                  <button className="browse-row" onClick={() => join(c)}>
                    <span className="row-main">
                      <span className="row-title">
                        {c.kind === "private" ? "🔒 " : "# "}
                        {c.name}
                      </span>
                      {c.topic && <span className="row-meta">{c.topic}</span>}
                    </span>
                    {c.membership ? <Badge>{t("Joined")}</Badge> : <span className="join">{t("Join")}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {tab === "create" && (
        <Form onSubmit={create}>
          <Field label={t("Name")} hint={t("Lowercase letters, digits, - and _. Like #design-reviews.")}>
            <input value={name} onChange={(e) => setName(e.target.value.toLowerCase())} placeholder={t("e.g. design-reviews")} aria-label={t("Channel name")} autoFocus required />
          </Field>
          <Switch checked={priv} onChange={setPriv} label={t("Private")} description={t("Only people who are added can find it and read it.")} />
          <div className="form-actions">
            <button type="submit">{t("Create")}</button>
          </div>
        </Form>
      )}

      {tab === "dm" && (
        <Form onSubmit={dm}>
          <Field label={t("People")} hint={t("Their handles, separated by commas. More than one makes a group conversation.")}>
            <input value={people} onChange={(e) => setPeople(e.target.value)} placeholder={t("@handle, @handle")} aria-label={t("People")} autoFocus required />
          </Field>
          <div className="form-actions">
            <button type="submit">{t("Open")}</button>
          </div>
        </Form>
      )}
    </Sheet>
  );
}
